import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  UserMarkingCodeItem,
  UserMarkingCodeFilters,
  SortField,
  UserFilterStatusOption,
  UserFilterTypeOption
} from '../types';
import {
  API_BASE,
  APP_VERSION,
  DEFAULT_USER_KM_TYPES,
  DEFAULT_USER_KM_STATUSES
} from '../constants';
import { Document } from '../../Documents/types';
import { notify } from '../../../notifications';

const INITIAL_FILTERS: UserMarkingCodeFilters = {
  search: '',
  direction: 'all',
  type: 'all',
  status: 'all',
  dateFrom: '',
  dateTo: '',
  selectedStorage: null,
  orderBy: 'date',
  order: 'desc'
};

export const useUserMarkingCodes = () => {
  const [items, setItems] = useState<UserMarkingCodeItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [filters, setFilters] = useState<UserMarkingCodeFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);

  const [storages, setStorages] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<UserFilterStatusOption[]>(DEFAULT_USER_KM_STATUSES);
  const [types, setTypes] = useState<UserFilterTypeOption[]>(DEFAULT_USER_KM_TYPES);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);

  // Состояние просмотра документа
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isDocLoading, setIsDocLoading] = useState<boolean>(false);
  const [isDocDialogOpen, setIsDocDialogOpen] = useState<boolean>(false);

  const userGuid = localStorage.getItem('guid') || '';
  const userRole = localStorage.getItem('role') || 'user';

  // Загрузка общих справочников (статусы, типы, склады)
  const fetchFilterOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/marking-codes/filter-options`, {
        params: { version: APP_VERSION }
      });

      if (response.data?.success && response.data?.data) {
        const {
          statuses: fetchedStatuses,
          types: fetchedTypes,
          storages: fetchedStorages
        } = response.data.data;

        if (Array.isArray(fetchedStorages) && fetchedStorages.length > 0) {
          setStorages(fetchedStorages);
        }

        if (Array.isArray(fetchedStatuses) && fetchedStatuses.length > 0) {
          setStatuses([{ zid: 'all', nameRu: 'Все' }, ...fetchedStatuses]);
        }

        if (Array.isArray(fetchedTypes) && fetchedTypes.length > 0) {
          setTypes([{ zid: 'all', nameRu: 'Все' }, ...fetchedTypes]);
        }
      }
    } catch (e) {
      console.warn('Справочники фильтров пользователя загружены по умолчанию:', e);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Загрузка списка кодов маркировки с сервера с фильтрацией по guid
  const fetchData = useCallback(async () => {
    if (!userGuid) {
      setItems([]);
      setTotalCount(0);
      notify.error('Не удалось определить идентификатор аптеки (GUID). Пожалуйста, авторизуйтесь заново.');
      return;
    }

    setIsLoading(true);
    try {
      const params: Record<string, any> = {
        guid: userGuid,
        role: userRole,
        direction: filters.direction,
        page: page + 1,
        limit: pageSize,
        orderBy: filters.orderBy,
        order: filters.order,
        version: APP_VERSION
      };

      if (filters.search) params.search = filters.search;
      if (filters.type && filters.type !== 'all') params.type = filters.type;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.selectedStorage) params.storage = filters.selectedStorage;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await axios.get(`${API_BASE}/user/marking-codes`, { params });

      if (response.data?.success) {
        setItems(response.data.data || []);
        setTotalCount(response.data.total || 0);
      } else {
        throw new Error(response.data?.message || 'Ошибка загрузки данных');
      }
    } catch (error: any) {
      console.error('Ошибка загрузки КМ пользователя:', error);
      setItems([]);
      setTotalCount(0);
      notify.error(error.response?.data?.message || error.message || 'Не удалось загрузить данные кодов маркировки');
    } finally {
      setIsLoading(false);
    }
  }, [userGuid, userRole, filters, page, pageSize]);

  // Запуск загрузки при изменении фильтров, страницы или размера страницы
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(() => {
    fetchFilterOptions();
    fetchData();
    notify.info('Список кодов маркировки обновлен');
  }, [fetchFilterOptions, fetchData]);

  // Выгрузка в Excel
  const handleExport = useCallback(async () => {
    if (!userGuid) {
      notify.error('Идентификатор аптеки не найден');
      return;
    }

    setIsExporting(true);
    try {
      const params: Record<string, any> = {
        guid: userGuid,
        role: userRole,
        direction: filters.direction,
        format: 'xlsx',
        version: APP_VERSION
      };

      if (filters.search) params.search = filters.search;
      if (filters.type && filters.type !== 'all') params.type = filters.type;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.selectedStorage) params.storage = filters.selectedStorage;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await axios.get(`${API_BASE}/user/marking-codes/export`, {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const nowStr = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `коды_маркировки_${nowStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      notify.success('Файл Excel успешно скачан');
    } catch (e: any) {
      console.error('Ошибка выгрузки:', e);
      notify.error('Не удалось сформировать файл выгрузки');
    } finally {
      setIsExporting(false);
    }
  }, [userGuid, userRole, filters]);

  // Открытие документа с проверкой прав владения
  const handleOpenDocument = useCallback(async (docNumber: string, rowStockIdIn?: string, rowStockIdOut?: string) => {
    if (!docNumber) return;

    const lowerGuid = (userGuid || '').toLowerCase();

    // Предварительная проверка по данным строки
    if (lowerGuid && rowStockIdIn && rowStockIdOut) {
      const isOwner = rowStockIdIn.toLowerCase() === lowerGuid || rowStockIdOut.toLowerCase() === lowerGuid;
      if (!isOwner) {
        notify.error('Документ не принадлежит вашей аптеке или складу');
        return;
      }
    }

    setIsDocLoading(true);
    setIsDocDialogOpen(true);

    try {
      const resp = await axios.get(`${API_BASE}/documents/${docNumber}`, {
        params: { version: APP_VERSION }
      });

      if (resp.data?.success && resp.data?.document) {
        const doc: Document = resp.data.document;

        // Строгая проверка прав владения по данным документа
        const docIn = (doc.stockIdIn || '').toLowerCase();
        const docOut = (doc.stockIdOut || '').toLowerCase();

        if (lowerGuid && docIn && docOut) {
          const isAllowed = docIn === lowerGuid || docOut === lowerGuid;
          if (!isAllowed) {
            setIsDocDialogOpen(false);
            setSelectedDocument(null);
            notify.error('Доступ запрещен: документ не принадлежит вашему аккаунту');
            return;
          }
        }

        setSelectedDocument(doc);
      } else {
        throw new Error(resp.data?.message || 'Документ не найден');
      }
    } catch (err: any) {
      console.error('Ошибка загрузки документа:', err);
      setIsDocDialogOpen(false);
      setSelectedDocument(null);
      notify.error(err.response?.data?.message || err.message || 'Не удалось открыть документ');
    } finally {
      setIsDocLoading(false);
    }
  }, [userGuid]);

  const handleCloseDocument = useCallback(() => {
    setIsDocDialogOpen(false);
    setSelectedDocument(null);
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<UserMarkingCodeFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSort = useCallback((field: SortField) => {
    setFilters((prev) => ({
      ...prev,
      orderBy: field,
      order: prev.orderBy === field ? (prev.order === 'asc' ? 'desc' : 'asc') : 'desc'
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(0);
  }, []);

  return {
    items,
    totalCount,
    isLoading,
    isExporting,
    filters,
    page,
    pageSize,
    userRole,
    storages,
    statuses,
    types,
    optionsLoading,
    selectedDocument,
    isDocLoading,
    isDocDialogOpen,
    setPage,
    setPageSize,
    handleRefresh,
    handleExport,
    handleFilterChange,
    handleSort,
    handleResetFilters,
    handleOpenDocument,
    handleCloseDocument
  };
};
